/* eslint-disable */
import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Card,
  CardContent,
  Divider,
  useTheme,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  People as PeopleIcon,
  Storage as StorageIcon,
  CalendarToday as CalendarIcon,
  Refresh as RefreshIcon,
  Business as BusinessIcon,
  Cloud as CloudIcon,
  Security as SecurityIcon,
  BarChart as BarChartIcon,
  Support as SupportIcon,
  Description as DescriptionIcon,
  Email as EmailIcon,
  AccessTime as AccessTimeIcon,
  Diamond as DiamondIcon,
} from "@mui/icons-material";
import { showSuccessToast, showErrorToast } from "utils/toastNotification";
import AddPlansModal from "./AddPlansModal";
import EditPlansModal from "./EditPlansModal";
import api from "services/api";
import plansSvg from "assets/folders/plans.svg";

const Plans = () => {
  const theme = useTheme();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [activeFilter, setActiveFilter] = useState("all");

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const response = await api.get("/v1/api/plans");
      if (response?.meta?.status === true) {
        setPlans(response.data);
      } else {
        showErrorToast("Failed to fetch plans");
      }
    } catch (error) {
      console.error("Error fetching plans:", error);
      showErrorToast("Error loading plans");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlans();
  }, []);

  const filteredPlans = plans.filter((plan) => {
    if (activeFilter === "all") return true;
    if (activeFilter === "monthly") return plan.billing_cycle === "monthly";
    if (activeFilter === "yearly") return plan.billing_cycle === "yearly";
    return true;
  });

  const formatPrice = (price, cycle) => {
    const numPrice = parseFloat(price);
    if (numPrice === 0) return "Free";
    return `₹${numPrice.toLocaleString()}`;
  };

  const isPopularPlan = (planName) => {
    return (
      planName?.toLowerCase().includes("pro") ||
      planName?.toLowerCase().includes("professional")
    );
  };

  const getFeatureCount = (plan) => {
    return plan.features?.filter((f) => f.is_enabled === 1).length || 0;
  };

  const getFeatureIcon = (featureKey) => {
    const icons = {
      auth: <SecurityIcon sx={{ fontSize: 14 }} />,
      tenant: <BusinessIcon sx={{ fontSize: 14 }} />,
      users: <PeopleIcon sx={{ fontSize: 14 }} />,
      employees: <PeopleIcon sx={{ fontSize: 14 }} />,
      attendance: <AccessTimeIcon sx={{ fontSize: 14 }} />,
      leave: <CalendarIcon sx={{ fontSize: 14 }} />,
      payroll: <DescriptionIcon sx={{ fontSize: 14 }} />,
      projects: <DescriptionIcon sx={{ fontSize: 14 }} />,
      reports: <BarChartIcon sx={{ fontSize: 14 }} />,
      notifications: <EmailIcon sx={{ fontSize: 14 }} />,
      max_users: <PeopleIcon sx={{ fontSize: 14 }} />,
      max_employees: <PeopleIcon sx={{ fontSize: 14 }} />,
      max_storage: <StorageIcon sx={{ fontSize: 14 }} />,
    };
    return icons[featureKey] || <CheckCircleIcon sx={{ fontSize: 14 }} />;
  };

  const filterButtons = [
    { key: "all", label: "All Plans" },
    { key: "monthly", label: "Monthly" },
    { key: "yearly", label: "Yearly" },
  ];

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background:
          theme.palette.mode === "dark"
            ? "linear-gradient(135deg, #0a0f1e 0%, #0f172a 50%, #1e1b2e 100%)"
            : "linear-gradient(135deg, #fffbeb 0%, #ffffff 50%, #fefce8 100%)",
        p: 3,
      }}
    >
      {/* Header with SVG */}
      <Box sx={{ mb: 4 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ sm: "center" }}
          justifyContent="space-between"
          sx={{ width: "100%" }}
        >
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            sx={{ flex: 1 }}
          >
            <Box
              component="img"
              src={plansSvg}
              alt="Plans"
              sx={{
                width: { xs: 64, lg: 80 },
                height: { xs: 64, lg: 80 },
                objectFit: "contain",
              }}
            />
            <Box>
              <Typography
                variant="h4"
                component="h1"
                sx={{
                  fontWeight: "bold",
                  color: "text.primary",
                }}
              >
                Pricing Plans
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  mt: 0.5,
                  color: "text.secondary",
                }}
              >
                Manage your subscription plans and pricing
              </Typography>
            </Box>
          </Stack>

          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setShowCreateModal(true)}
            sx={{
              background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
              borderRadius: "10px",
              px: 2.5,
              py: 0.8,
              height: "38px",
              fontSize: "0.875rem",
              fontWeight: 500,
              textTransform: "none",
              minWidth: "auto",
              whiteSpace: "nowrap",
              boxShadow: "none",
              transition: "all 0.2s ease",
              "&:hover": {
                background: "linear-gradient(135deg, #d97706 0%, #b45309 100%)",
                boxShadow: "0 2px 8px rgba(245, 158, 11, 0.3)",
                transform: "translateY(-1px)",
              },
              "&:active": {
                transform: "translateY(0)",
              },
            }}
          >
            Create Plan
          </Button>
        </Stack>
      </Box>

      {/* Filters */}
      <Stack direction="row" spacing={1} sx={{ mb: 4 }}>
        {filterButtons.map((filter) => (
          <Button
            key={filter.key}
            variant={activeFilter === filter.key ? "contained" : "outlined"}
            onClick={() => setActiveFilter(filter.key)}
            sx={{
              textTransform: "none",
              borderRadius: "8px",
              px: 3,
              py: 0.7,
              fontSize: "0.875rem",
              fontWeight: 500,
              ...(activeFilter === filter.key && {
                background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                boxShadow: "0 4px 6px -1px rgba(245, 158, 11, 0.25)",
                "&:hover": {
                  background:
                    "linear-gradient(135deg, #d97706 0%, #b45309 100%)",
                },
              }),
              ...(activeFilter !== filter.key && {
                borderColor:
                  theme.palette.mode === "dark" ? "#475569" : "#d1d5db",
                color: theme.palette.mode === "dark" ? "#d1d5db" : "#6b7280",
                "&:hover": {
                  borderColor: "#f59e0b",
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? "rgba(245,158,11,0.1)"
                      : "#fffbeb",
                },
              }),
            }}
          >
            {filter.label}
          </Button>
        ))}
      </Stack>

      {/* Loading State */}
      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 400,
          }}
        >
          <Stack alignItems="center" spacing={2}>
            <CircularProgress
              size={48}
              sx={{
                color: "primary.main",
                "& .MuiCircularProgress-circle": {
                  strokeLinecap: "round",
                },
              }}
            />
            <Typography
              sx={{
                color: "text.secondary",
              }}
            >
              Loading plans...
            </Typography>
          </Stack>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {filteredPlans.map((plan, index) => {
            const popular = isPopularPlan(plan.name);
            return (
              <Grid item xs={12} sm={6} md={6} lg={4} xl={3} key={plan.id}>
                {" "}
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1, duration: 0.5 }}
                  style={{ height: "100%" }}
                >
                  <Card
                    elevation={0}
                    sx={{
                      position: "relative",
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      borderRadius: "16px",
                      background:
                        theme.palette.mode === "dark"
                          ? "linear-gradient(135deg, #1e293b 0%, #0f172a 100%)"
                          : "linear-gradient(135deg, #ffffff 0%, #fffbeb 100%)",
                      boxShadow: popular
                        ? "0 20px 40px -12px rgba(245, 158, 11, 0.25)"
                        : "0 10px 25px -10px rgba(0, 0, 0, 0.1)",
                      border: `1px solid ${
                        theme.palette.mode === "dark" ? "#334155" : "#fef3c7"
                      }`,
                      transition: "all 0.3s ease",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow: popular
                          ? "0 25px 50px -12px rgba(245, 158, 11, 0.35)"
                          : "0 20px 40px -12px rgba(0, 0, 0, 0.15)",
                      },
                    }}
                  >
                    {/* Yellow Banner at Top */}
                    <Box
                      sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        height: 8,
                        borderRadius: "16px 16px 0 0",
                        background:
                          "linear-gradient(90deg, #fbbf24 0%, #f59e0b 50%, #d97706 100%)",
                      }}
                    />

                    {/* Popular Crown Badge */}
                    {popular && (
                      <Chip
                        icon={
                          <DiamondIcon
                            sx={{ fontSize: 14, color: "#fef08a" }}
                          />
                        }
                        label="Popular"
                        sx={{
                          position: "absolute",
                          top: -12,
                          left: "50%",
                          transform: "translateX(-50%)",
                          backgroundColor:
                            "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                          color: "#ffffff",
                          fontWeight: "bold",
                          fontSize: "0.7rem",
                          height: 24,
                          "& .MuiChip-label": { px: 1.5 },
                          zIndex: 1,
                        }}
                      />
                    )}

                    <CardContent
                      sx={{
                        p: 3,
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      {/* Plan Name & Billing Cycle */}
                      <Box sx={{ textAlign: "center", mb: 2 }}>
                        <Typography
                          variant="h6"
                          component="h3"
                          sx={{
                            fontWeight: "bold",
                            color: "text.primary",
                          }}
                        >
                          {plan.name}
                        </Typography>
                        <Chip
                          label={
                            plan.billing_cycle === "monthly"
                              ? "Monthly Plan"
                              : "Yearly Plan"
                          }
                          size="small"
                          sx={{
                            mt: 1,
                            backgroundColor:
                              theme.palette.mode === "dark"
                                ? "rgba(245,158,11,0.2)"
                                : "#fef3c7",
                            color: "primary.main",
                            fontSize: "0.65rem",
                            fontWeight: 600,
                            height: 20,
                          }}
                        />
                      </Box>

                      {/* Price */}
                      <Box
                        sx={{
                          textAlign: "center",
                          borderBottom: `1px solid ${
                            theme.palette.mode === "dark"
                              ? "#334155"
                              : "#f3f4f6"
                          }`,
                          pb: 2,
                          mb: 2,
                        }}
                      >
                        <Stack
                          direction="row"
                          alignItems="baseline"
                          justifyContent="center"
                        >
                          <Typography
                            variant="h3"
                            component="span"
                            sx={{
                              fontWeight: "extrabold",
                              color: "text.primary",
                            }}
                          >
                            {formatPrice(plan.price, plan.billing_cycle)}
                          </Typography>
                          {plan.price !== "0.00" && (
                            <Typography
                              variant="caption"
                              sx={{
                                color: "text.secondary",
                                ml: 0.5,
                              }}
                            >
                              /{plan.billing_cycle === "monthly" ? "mo" : "yr"}
                            </Typography>
                          )}
                        </Stack>
                        {plan.description && (
                          <Typography
                            variant="caption"
                            sx={{
                              mt: 1,
                              display: "block",
                              color: "text.secondary",
                            }}
                          >
                            {plan.description}
                          </Typography>
                        )}
                      </Box>

                      {/* Limits Grid */}
                      <Box sx={{ mb: 2 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: "semibold",
                            letterSpacing: "0.05em",
                            color:
                              theme.palette.mode === "dark"
                                ? "#6b7280"
                                : "#9ca3af",
                          }}
                        >
                          PLAN LIMITS
                        </Typography>
                        <Grid container spacing={1} sx={{ mt: 1 }}>
                          {plan.max_users > 0 && (
                            <Grid item xs={6}>
                              <Stack
                                direction="row"
                                spacing={0.5}
                                alignItems="center"
                              >
                                <PeopleIcon
                                  sx={{ fontSize: 14, color: "primary.main" }}
                                />
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color:
                                      theme.palette.mode === "dark"
                                        ? "#d1d5db"
                                        : "#4b5563",
                                  }}
                                >
                                  <strong>{plan.max_users}</strong> Users
                                </Typography>
                              </Stack>
                            </Grid>
                          )}
                          {plan.max_employees > 0 && (
                            <Grid item xs={6}>
                              <Stack
                                direction="row"
                                spacing={0.5}
                                alignItems="center"
                              >
                                <PeopleIcon
                                  sx={{ fontSize: 14, color: "primary.main" }}
                                />
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color:
                                      theme.palette.mode === "dark"
                                        ? "#d1d5db"
                                        : "#4b5563",
                                  }}
                                >
                                  <strong>{plan.max_employees}</strong>{" "}
                                  Employees
                                </Typography>
                              </Stack>
                            </Grid>
                          )}
                          {plan.max_storage_mb > 0 && (
                            <Grid item xs={6}>
                              <Stack
                                direction="row"
                                spacing={0.5}
                                alignItems="center"
                              >
                                <StorageIcon
                                  sx={{ fontSize: 14, color: "primary.main" }}
                                />
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color:
                                      theme.palette.mode === "dark"
                                        ? "#d1d5db"
                                        : "#4b5563",
                                  }}
                                >
                                  <strong>{plan.max_storage_mb}</strong> MB
                                </Typography>
                              </Stack>
                            </Grid>
                          )}
                          {plan.trial_days > 0 && (
                            <Grid item xs={6}>
                              <Stack
                                direction="row"
                                spacing={0.5}
                                alignItems="center"
                              >
                                <CalendarIcon
                                  sx={{ fontSize: 14, color: "primary.main" }}
                                />
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color:
                                      theme.palette.mode === "dark"
                                        ? "#d1d5db"
                                        : "#4b5563",
                                  }}
                                >
                                  <strong>{plan.trial_days}</strong> Days Trial
                                </Typography>
                              </Stack>
                            </Grid>
                          )}
                        </Grid>
                      </Box>

                      {/* Features List */}
                      {plan.features && plan.features.length > 0 && (
                        <Box sx={{ flex: 1, mb: 2 }}>
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                            sx={{ mb: 1 }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                fontWeight: "semibold",
                                letterSpacing: "0.05em",
                                color:
                                  theme.palette.mode === "dark"
                                    ? "#6b7280"
                                    : "#9ca3af",
                              }}
                            >
                              FEATURES
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{ color: "primary.main" }}
                            >
                              {getFeatureCount(plan)} included
                            </Typography>
                          </Stack>
                          <Stack spacing={1}>
                            {plan.features.map((feature) => (
                              <Stack
                                key={feature.id}
                                direction="row"
                                spacing={1}
                                alignItems="center"
                              >
                                <Box
                                  sx={{
                                    color: "#10b981",
                                    display: "flex",
                                    alignItems: "center",
                                  }}
                                >
                                  {getFeatureIcon(feature.feature_key)}
                                </Box>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    flex: 1,
                                    textTransform: "capitalize",
                                    color:
                                      theme.palette.mode === "dark"
                                        ? "#d1d5db"
                                        : "#4b5563",
                                  }}
                                >
                                  {feature.feature_key.replace(/_/g, " ")}
                                </Typography>
                                {feature.is_enabled === 1 ? (
                                  <CheckCircleIcon
                                    sx={{ fontSize: 12, color: "#10b981" }}
                                  />
                                ) : (
                                  <CancelIcon
                                    sx={{ fontSize: 12, color: "#d1d5db" }}
                                  />
                                )}
                              </Stack>
                            ))}
                          </Stack>
                        </Box>
                      )}

                      {/* Action Button */}
                      <Button
                        variant="outlined"
                        startIcon={<EditIcon />}
                        onClick={() => {
                          setSelectedPlan(plan);
                          setShowEditModal(true);
                        }}
                        fullWidth
                        sx={{
                          mt: 2,
                          borderRadius: "12px",
                          py: 1,
                          textTransform: "none",
                          fontWeight: 500,
                          borderColor: "#f59e0b",
                          color: "primary.main",
                          "&:hover": {
                            borderColor: "#d97706",
                            backgroundColor:
                              theme.palette.mode === "dark"
                                ? "rgba(245,158,11,0.1)"
                                : "#fffbeb",
                          },
                        }}
                      >
                        Edit Plan
                      </Button>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Empty State */}
      {!loading && filteredPlans.length === 0 && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: 400,
            borderRadius: "16px",
            border: `2px dashed ${
              theme.palette.mode === "dark" ? "#334155" : "#d1d5db"
            }`,
            backgroundColor:
              theme.palette.mode === "dark"
                ? "rgba(30,41,59,0.5)"
                : "rgba(255,255,255,0.5)",
          }}
        >
          <RefreshIcon sx={{ fontSize: 48, color: "#9ca3af", mb: 2 }} />
          <Typography
            sx={{
              color: "text.secondary",
              mb: 2,
            }}
          >
            No plans found
          </Typography>
          <Button
            variant="contained"
            onClick={() => setShowCreateModal(true)}
            sx={{
              borderRadius: "8px",
              background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
              textTransform: "none",
            }}
          >
            Create your first plan
          </Button>
        </Box>
      )}

      {/* Modals */}
      <AddPlansModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={fetchPlans}
      />

      <EditPlansModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedPlan(null);
        }}
        plan={selectedPlan}
        onSuccess={fetchPlans}
      />
    </Box>
  );
};

export default Plans;
